import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { useRuntimeConstants } from "../runtimeConstants/RuntimeConstantsContext";
import { useFeaturesReady } from "../featuresReady/FeaturesReadyContext";
import { getFeature } from "../../utils/features";
import { isProductUnavailable } from "../../../utils/productUtils";
import { Product } from "../../types/productTypes/product";
import {
  StylingLookDataFromServer,
  StylingLook,
  StylingLookWithStockPrice,
} from "../../types/productTypes/stylingLookProduct";

interface WaysToStyleContextType {
  shouldShowWaysToStyle: boolean;
  stylingLooks: StylingLook[];
  stockPriceByLookIndex: Record<number, StylingLookWithStockPrice>;
  setStockPriceForLook: (
    index: number,
    data: StylingLookWithStockPrice
  ) => void;
  resetStockPriceData: () => void;
}

export const WaysToStyleContext = createContext<WaysToStyleContextType>({
  shouldShowWaysToStyle: false,
  stylingLooks: [],
  stockPriceByLookIndex: {},
  setStockPriceForLook: () => {},
  resetStockPriceData: () => {},
});

export const isWaysToStyleFeatureEnabled = () =>
  getFeature("pdp-ways-to-style")?.isEnabled();

export const WaysToStyleProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isMobile, product, stylingLooksData, isMixAndMatch } =
    useRuntimeConstants() as {
      isMobile: boolean;
      product: Product;
      stylingLooksData: StylingLookDataFromServer[];
      isMixAndMatch: boolean;
    };

  const [shouldShowWaysToStyle, setShouldShowWaysToStyle] = useState(false);
  const [stylingLooks, setStylingLooks] = useState<StylingLook[]>([]);
  const [stockPriceByLookIndex, setStockPriceByLookIndex] = useState<
    Record<number, StylingLookWithStockPrice>
  >({});
  const { areFeaturesReady } = useFeaturesReady() as {
    areFeaturesReady: boolean;
  };

  const [isWaysToStyleEnabled, setIsWaysToStyleEnabled] = useState(false);

  const setStockPriceForLook = useCallback(
    (index: number, data: StylingLookWithStockPrice) => {
      setStockPriceByLookIndex((prev) => ({ ...prev, [index]: data }));
    },
    []
  );

  const resetStockPriceData = useCallback(() => {
    setStockPriceByLookIndex({});
  }, []);

  useEffect(() => {
    if (areFeaturesReady) {
      setIsWaysToStyleEnabled(isWaysToStyleFeatureEnabled());
    }
  }, [areFeaturesReady]);

  useEffect(() => {
    if (isMobile && areFeaturesReady && isWaysToStyleEnabled) {
      setShouldShowWaysToStyle(true);
    }
  }, [areFeaturesReady, isWaysToStyleEnabled, isMobile]);

  useEffect(() => {
    const updateStylingLooks = () => {
      const isUnavailable = isProductUnavailable(product);
      const shouldMapStylingLooks =
        stylingLooksData?.length && !isMixAndMatch && !isUnavailable;

      if (shouldMapStylingLooks) {
        const mappedLooks = mapStylingLooks(stylingLooksData);

        setStylingLooks(mappedLooks);
      }
    };

    if (areFeaturesReady && isWaysToStyleEnabled) {
      updateStylingLooks();
    }
  }, [
    areFeaturesReady,
    isWaysToStyleEnabled,
    stylingLooksData,
    product,
    isMixAndMatch,
  ]);

  return (
    <WaysToStyleContext.Provider
      value={{
        shouldShowWaysToStyle,
        stylingLooks,
        stockPriceByLookIndex,
        setStockPriceForLook,
        resetStockPriceData,
      }}
    >
      {children}
    </WaysToStyleContext.Provider>
  );
};

const mapStylingLooks = (
  stylingLooksData: StylingLookDataFromServer[]
): StylingLook[] => {
  if (!stylingLooksData) {
    return [];
  }

  return stylingLooksData
    .map((look) => ({
      isHeroLook: look.isHeroLook,
      labelTranslationKey: look.labelTranslationKey,
      products:
        look.looksProductsFromServer ||
        look.stylingLookProductsFromServer ||
        [],
    }))
    .filter(
      (look) =>
        look.products.length && look.products.some((product) => product.isHero)
    )
    .sort((a, b) => Number(b.isHeroLook) - Number(a.isHeroLook));
};

export const useWaysToStyle = () => useContext(WaysToStyleContext);
